import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose } from "../../Components/UI/dialog";
import { Button } from "../../Components/UI/button";
import { X, Check, ChevronDown, ArrowLeft } from "lucide-react";
import { useState, useEffect } from "react";
import { cn } from "../../Lib/Cn";
import { Textarea } from "../UI/textarea";
import { Label } from "../UI/label";
import useIsMobile from "../../Hooks/useIsMobile";
import { manageRecordAPI } from "../../Modules/Records/RecordAPI";
import { toast } from "sonner";
import { getAuthData } from '../../Utils/AuthUtils';
import { Tooltip, TooltipTrigger, TooltipContent } from "../../Components/UI/tooltip";
import { getWspTemplatesByType } from "../Enums/DataEnums";

const WspDialog = ({
    isOpen = false,
    onClose,
    connectionData = {},
    phone = "",
    type = "CONTACT",
}) => {
    const [dialogOpen, setDialogOpen] = useState(isOpen);
    const [isSending, setIsSending] = useState(false);
    const [showAllTemplates, setShowAllTemplates] = useState(false);
    const authData = getAuthData();

    const [selectedTemplateId, setSelectedTemplateId] = useState(null);
    const [message, setMessage] = useState("");
    const wspTemplates = getWspTemplatesByType(type);
    const isMobile = useIsMobile();

    useEffect(() => {
        setDialogOpen(isOpen);
    }, [isOpen]);

    // Load preferred template on component mount
    useEffect(() => {
        if (isOpen && wspTemplates.length > 0) {
            let finalTemplate = null;
            const prefKey = `${type}PrefTID`;
            const prefTID = localStorage.getItem(prefKey);
            if (prefTID) {
                finalTemplate = wspTemplates.find(t => t.TID === parseInt(prefTID));
            }
            // If no preferred template is found or valid, default to the first available template
            if (!finalTemplate) {
                finalTemplate = wspTemplates[0];
            }

            // If a template is selected (either preferred or default), set the state
            if (finalTemplate) {
                setSelectedTemplateId(finalTemplate.TID);
                const processedMessage = replaceTemplateVariables(finalTemplate.Desc);
                setMessage(processedMessage);
            } else {
                // If no templates are available at all, clear the state
                setSelectedTemplateId(null);
                setMessage("");
            }
        }
    }, [isOpen, type]);

    // Replace template variables with actual values
    const replaceTemplateVariables = (templateText) => {
        if (!templateText) return "";

        let result = templateText;

        result = result.replace(/{{CONN_CONTACT_NAME}}/g, connectionData?.contactName || "");
        result = result.replace(/{{CONN_COMPANY_NAME}}/g, connectionData?.bizName || "");
        result = result.replace(/{{OUR_NAME}}/g, authData.UserName || "");
        result = result.replace(/{{OUR_COMPANY_NAME}}/g, authData?.AgencyName || "");

        return result;
    };

    // Check if template has all required variables available
    const hasAllRequiredData = (templateText) => {
        if (!templateText) return false;

        if (templateText.includes("{{CONN_CONTACT_NAME}}") && !connectionData?.contactName) { return false; }
        if (templateText.includes("{{CONN_COMPANY_NAME}}") && !connectionData?.bizName) { return false; }
        if (templateText.includes("{{OUR_NAME}}") && !authData.UserName) { return false; }
        if (templateText.includes("{{OUR_COMPANY_NAME}}") && !authData?.AgencyName) { return false; }

        return true;
    };

    // Get filtered templates
    const filteredTemplates = wspTemplates && wspTemplates.length > 0 ? wspTemplates.filter((template) => hasAllRequiredData(template.Desc)) : [];

    //   // Sort templates: preferred one first, then others
    //   const sortedFilteredTemplates = filteredTemplates.sort((a, b) => {
    //     const prefTID = localStorage.getItem("prefTID");
    //     if (prefTID) {
    //       if (a.TID === parseInt(prefTID)) return -1;
    //       if (b.TID === parseInt(prefTID)) return 1;
    //     }
    //     return 0;
    //   });

    // Display logic for mobile
    //   const wspTemplates = isMobile && !showAllTemplates ? sortedFilteredTemplates.slice(0, 1) : sortedFilteredTemplates;

    // Handle template selection
    const handleTemplateSelect = (e, template) => {
        setSelectedTemplateId(template.TID);
        const processedMessage = replaceTemplateVariables(template.Desc);
        setMessage(processedMessage);
    };

    // Handle close
    const handleClose = (e) => {
        setSelectedTemplateId(null);
        setMessage("");
        setShowAllTemplates(false);
        setDialogOpen(false);
        onClose();
    };

    // Save WhatsApp message as a record
    const saveWhatsAppRecord = async () => {
        try {
            const recordData = {
                connID: connectionData?.connID || 0,
                pocUserID: authData?.UserID || 0,
                recType: 'Log',
                recDT: new Date().toISOString(),
                recNotes: `WhatsApp message: ${message}`,
                recThreadIDs: connectionData?.threadID || '',
            };

            const result = await manageRecordAPI(recordData, null, 'ADD', 0);

            if (!result.error) {
                return true;
            } else {
                toast.error("Failed to log WhatsApp message", { description: result.message });
                return false;
            }
        } catch (error) {
            console.error("Error saving WhatsApp record:", error);
            toast.error("Error logging WhatsApp message", {
                description: error.message
            });
            return false;
        }
    };

    // Handle send
    const handleSend = async (e, shouldSaveRecord = false) => {
        e.stopPropagation();

        if (!message.trim()) {
            toast.error("Message is empty", { description: "Please enter a message before sending." });
            return;
        }

        if (!connectionData?.connID) {
            toast.error("Connection missing", { description: "No connection data found." });
            return;
        }

        setIsSending(true);

        try {
            // Save the selected template ID to localStorage with type-based key
            if (selectedTemplateId) {
                const prefKey = `${type}PrefTID`;
                localStorage.setItem(prefKey, selectedTemplateId.toString());
            }

            if (shouldSaveRecord) {
                const recordSaved = await saveWhatsAppRecord();
                if (!recordSaved) {
                    toast.error("Failed to save the record.");
                    setIsSending(false);
                    return;
                }
            }

            const whatsappUrl = `https://wa.me/${connectionData?.cc}${phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`;
            window.open(whatsappUrl, "_blank");

            handleClose();
        } catch (error) {
            console.error("Error sending WhatsApp message:", error);
            toast.error("Failed to send message", {
                description: error.message
            });
        } finally {
            setIsSending(false);
        }
    };

    const handleInternalClick = (e) => {
        e.stopPropagation();
    };

    return (
        <Dialog open={dialogOpen} preventOutsideClick={true} onOpenChange={setDialogOpen}>
            <DialogContent
                className={cn(
                    "max-h-[100vh] h-full p-0 rounded-none sahi-radius overflow-hidden flex flex-col",
                    isMobile ? "w-full" : "sm:max-w-2xl lg:max-w-4xl"
                )}
            >
                <div className="flex flex-col h-full" onClick={handleInternalClick}>
                    {/* Header */}
                    <DialogHeader >
                        {isMobile && (
                            <div className="flex items-center gap-1 w-full">
                                <DialogClose onClick={handleClose} tabIndex={-1} className="p-0 w-auto h-auto flex-shrink-0 flex justify-center">
                                    <ArrowLeft className="h-[24px] w-[24px]" />
                                    <span className="sr-only">Back</span>
                                </DialogClose>
                                <DialogTitle className="text-lg font-medium truncate">
                                    Send Reminder
                                </DialogTitle>
                            </div>
                        )}
                        {!isMobile && (
                            <>
                                <DialogTitle>Send Reminder - {connectionData.bizName}</DialogTitle>
                                <DialogClose asChild onClick={handleClose}>
                                    <Button
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 p-0 sahi-radius hover:bg-white/20"
                                    >
                                        <X className="h-4 w-4" />
                                        <span className="sr-only">Close</span>
                                    </Button>
                                </DialogClose>
                            </>
                        )}
                    </DialogHeader>

                    <div className={cn("flex flex-1", isMobile ? "flex-col" : "flex-row")}>
                        {isMobile ? (
                            <>
                                <div className="w-full flex flex-col">
                                    <div className="p-4">
                                        <h3 className="text-lg font-semibold text-color">Templates</h3>
                                        <div className="space-y-2 mt-2 max-h-[180px] overflow-y-auto custom-scroll">
                                            {wspTemplates && wspTemplates.length > 0 ? (
                                                wspTemplates.map((template) => (
                                                    <button
                                                        key={template.TID}
                                                        onClick={(e) => handleTemplateSelect(e, template)}
                                                        className={cn(
                                                            "w-full text-left p-3 sahi-radius text-sm transition-all bg-white text-gray-800",
                                                            selectedTemplateId === template.TID
                                                                ? "border border-[#0d1f2d]"
                                                                : "border border-gray-200 "
                                                        )}
                                                    >
                                                        <p className="font-medium">{template.Title}</p>
                                                        <p className="text-xs mt-1 line-clamp-2 opacity-75">{replaceTemplateVariables(template.Desc)}</p>
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-500 text-center">No templates available</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                {/* Message Editor Section (Mobile) */}
                                <div className="flex flex-col px-4 pt-4 pb-3">
                                    <div className="mb-2">
                                        <Label className="text-lg font-semibold text-color">
                                            Remind to {connectionData?.bizName || "Contact"}
                                        </Label>
                                        <p className="text-xs text-muted-foreground mt-1 mb-2">Phone: +{connectionData?.cc} {phone}</p>
                                    </div>
                                    <Textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onClick={handleInternalClick}
                                        onFocus={(e) => e.stopPropagation()}
                                        placeholder="e.g. Hello, I hope you're doing well"
                                        className="flex-1 resize-none border-2 sahi-input transition-all mb-2 text-sm sahi-radius min-h-[150px]"
                                        allowResize={false}
                                        maxLength={200}
                                        autoFocus
                                    />
                                    {message.length >= 185 && (
                                        <span className="text-xs text-gray-500 mb-2">{200 - message.length} characters left</span>
                                    )}
                                    <div className="mt-2">
                                        <Button
                                            onClick={(e) => handleSend(e, false)}
                                            disabled={!message.trim() || isSending}
                                            className="sahi-primary-button mr-2"
                                        >
                                            <Check className="h-4 w-4" strokeWidth={3} />
                                            Send
                                        </Button>
                                        <Button
                                            variant="outline"
                                            onClick={handleClose}
                                            className="sahi-secondary-button flex-1"
                                            disabled={isSending}
                                        >
                                            <X className="w-3 h-3" />
                                            Close
                                        </Button>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <>
                                {/* Message Editor Section (Desktop) */}
                                <div className="flex flex-1 flex-col px-4 pt-4 pb-3">
                                    <div className="mb-2">
                                        <Label className="text-lg font-semibold text-color">
                                            Remind to {connectionData?.bizName || "Contact"}
                                        </Label>
                                        <p className="text-xs text-muted-foreground mt-1 mb-2">Phone: +{connectionData?.cc} {phone}</p>
                                    </div>
                                    <Textarea
                                        value={message}
                                        onChange={(e) => setMessage(e.target.value)}
                                        onClick={handleInternalClick}
                                        onFocus={(e) => e.stopPropagation()}
                                        placeholder="e.g. Hello, I hope you're doing well"
                                        className="flex-1 resize-none border-2 sahi-input transition-all mb-2 text-sm sahi-radius min-h-[150px]"
                                        allowResize={false}
                                        maxLength={200}
                                        autoFocus
                                    />
                                    {message.length >= 185 && (
                                        <span className="text-xs text-gray-500 mb-2">{200 - message.length} characters left</span>
                                    )}
                                    <div className="flex gap-3 mt-2">
                                        <Button
                                            onClick={(e) => handleSend(e, false)}
                                            disabled={!message.trim() || isSending}
                                            className="sahi-primary-button"
                                        >
                                            <Check className="h-4 w-4" strokeWidth={3} />
                                            Send
                                        </Button>
                                        {/* <Button
                                            onClick={(e) => handleSend(e, true)}
                                            disabled={!message.trim() || isSending}
                                            className="sahi-primary-button"
                                        >
                                            <Check className="h-4 w-4" strokeWidth={3} />
                                            Send & Save
                                        </Button> */}
                                        <Button
                                            variant="outline"
                                            onClick={handleClose}
                                            className="sahi-secondary-button"
                                            disabled={isSending}
                                        >
                                            <X className="w-3 h-3" />
                                            Close
                                        </Button>
                                    </div>
                                </div>

                                {/* Template Section (Desktop) */}
                                <div className="w-2/4 flex flex-col">
                                    <div className="p-4 pb-2">
                                        <h3 className="text-lg font-semibold text-color">Templates</h3>
                                    </div>
                                    <div className="overflow-y-auto custom-scroll flex-1 px-4 pb-4 max-h-[300px]">
                                        <div className="space-y-2">
                                            {wspTemplates && wspTemplates.length > 0 ? (
                                                wspTemplates.map((template) => (
                                                    <button
                                                        key={template.TID}
                                                        onClick={(e) => handleTemplateSelect(e, template)}
                                                        className={cn(
                                                            "w-full text-left p-3 sahi-radius text-sm transition-all bg-white text-gray-800",
                                                            selectedTemplateId === template.TID
                                                                ? "border border-[#0d1f2d]"
                                                                : "border border-gray-200 "
                                                        )}
                                                    >
                                                        <p className="font-medium">{template.Title}</p>
                                                        <p className="text-xs mt-1 line-clamp-2 opacity-75">{replaceTemplateVariables(template.Desc)}</p>
                                                    </button>
                                                ))
                                            ) : (
                                                <p className="text-sm text-gray-500 text-center">No templates available</p>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default WspDialog;